import { useContext } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { Button } from '@/components/button/Button'
import { RootState } from '@/store/store'
import { setStripePortalDialogVisible } from '@/store/slices/ui-state.slice'
import { StripeSubscriptionInterval } from '../store/schemas'
import { formatCentsToDollarsCompact } from '@/util/money'
import { AuthContext } from '@/context/auth-context'
import { selectDisplayCostPerSeatInCents, selectHasForeverPercentDiscount } from '../store/selectors'
import PricingInformationLink from './PricingInformationLink'

/**
 * Subscription Details
 *
 * Displays the subscription details for the user based on:
 * - The user's account data (e.g. account type, email address)
 * - The user's stripe subscription data (e.g. subscription status, subscription interval, subscription price and discounts)
 */
export default function SubscriptionDetails() {
  const dispatch = useAppDispatch()

  // User account data
  const { user, userAccountData } = useContext(AuthContext)
  const canViewSubscriptionInfo = userAccountData?.canViewStripeSubscriptionInfo
  const subscriptionStatusText = userAccountData?.subscriptionStatusDisplayName ?? 'Not available'

  // Redux state
  const subscriptionInterval = useAppSelector((state: RootState) => state.accountState.stripeSubscriptionInterval)
  const subscriptionUnitPrice = useAppSelector((state: RootState) => state.accountState.costPerSeatInCents)
  const hasDiscount = useAppSelector((state: RootState) => state.accountState.hasDiscount)
  const discountPercent = useAppSelector((state: RootState) => state.accountState.appliedCoupon?.percent_off)
  const hasForeverPercentDiscount = useAppSelector(selectHasForeverPercentDiscount)
  const displayCostPerSeatInCents = useAppSelector(selectDisplayCostPerSeatInCents)
  const hasLegacyStudentPlan = useAppSelector((state: RootState) => state.accountState.hasLegacyStudentPlan)

  // Inferred values
  const isSubscriptionBillingInfoAvailable = subscriptionUnitPrice !== null && displayCostPerSeatInCents !== null && subscriptionInterval !== null
  const showDisclaimer = hasDiscount && !hasForeverPercentDiscount

  const priceText = displayCostPerSeatInCents != null ? `${formatCentsToDollarsCompact(displayCostPerSeatInCents)} / ${subscriptionInterval}` : ''
  const originalPriceText = subscriptionUnitPrice != null ? `${formatCentsToDollarsCompact(subscriptionUnitPrice)} / ${subscriptionInterval}` : ''
  const studentPlanLabel = hasLegacyStudentPlan ? '(student plan) ' : ''

  return (
    <div className="px-6 py-4 bg-brand-neutral-50 shadow rounded-lg">
      <h2 className="mb-2 font-semibold">Subscription Details</h2>

      {/* Billing Subscription Info - only display if billing info is available */}
      {canViewSubscriptionInfo && isSubscriptionBillingInfoAvailable ? (
        <div className="flex flex-col gap-1 font-medium text-sm text-brand-neutral-600">
          {/* Show striked-through original price if there is a forever percent discount */}
          {hasForeverPercentDiscount && discountPercent != null && (
            <div className="flex gap-2 items-center">
              <p className="line-through">{originalPriceText}</p>
              <div className="px-1 py-[1px] rounded text-xs bg-brand-neutral-200 border border-brand-neutral-300">{discountPercent}% OFF</div>
            </div>
          )}
          {/* Pricing display */}
          <p>
            {`${priceText} ${studentPlanLabel}- `}
            {/* If we need to show the promotion disclaimer, move the Pricing Information link beside the Manage Subscription button */}
            {showDisclaimer ? (
              <span className="text-brand-800">Price shown before promotion</span>
            ) : (
              <PricingInformationLink label="Additional Pricing Information" className="font-semibold" />
            )}
          </p>
          <p>Billed {subscriptionInterval === StripeSubscriptionInterval.MONTH ? 'monthly' : 'annually'}, cancel anytime.</p>
        </div>
      ) : (
        // If the subscription billing info is not available, display only the link to the pricing page
        <PricingInformationLink className="font-semibold" />
      )}

      {/* Only render this section if either of the buttons should be shown */}
      {(canViewSubscriptionInfo || showDisclaimer) && (
        <div className="flex flex-row gap-2.5 mt-3 items-center">
          {/* Manage Subscription button - only show if the user has access to view subscription info */}
          {canViewSubscriptionInfo && (
            <Button variant="filled" color="primary" size="md" onClick={() => dispatch(setStripePortalDialogVisible(true))}>
              Manage Subscription
            </Button>
          )}
          {/* Pricing Information link - moved beside the Manage Subscription button here if a disclaimer is shown above */}
          {showDisclaimer && (
            <div className="px-2">
              <PricingInformationLink label="Additional Pricing Information" className="font-semibold" />
            </div>
          )}
        </div>
      )}

      <hr className="mt-3" />

      {/* Non-Billing Subscription Info */}
      <div className="pt-1">
        <dl className="divide-y divide-brand-neutral-100 divide">
          <div className="py-2">
            <dt className="text-sm font-bold text-brand-neutral-900">Status</dt>
            <dd className="text-sm text-brand-neutral-700">{subscriptionStatusText}</dd>
          </div>
          <div className="py-2">
            <dt className="text-sm font-bold text-brand-neutral-900">Account Type</dt>
            <dd className="text-sm text-brand-neutral-700">{hasLegacyStudentPlan ? 'Education' : 'Standard'}</dd>
          </div>
          <div className="pt-2">
            <dt className="text-sm font-bold text-brand-neutral-900">Email address</dt>
            <dd className="text-sm text-brand-neutral-700">{user?.email ?? 'N/A'}</dd>
          </div>
        </dl>
      </div>
    </div>
  )
}
